import { z } from "../zod.js";

// Module 05 — Waste & recovery domain events (spec 05 §6). The typed catalog of
// the events this module EMITS today, plus the set it CONSUMES.
//
// SLICE 05-A declared ONE event; SLICE 05-B adds the SIX ledger/remnant events
// — each joining WITH its emitter in the same change (`module-catalog-parity`
// gates this list against the emitter source BOTH WAYS). Spec 05 §6's remaining
// 10 rows (variance, match, PVC, regrind, sale, target, posting) belong to
// slices 05-C/05-D and join with their emitters.
//
// NOT ONE of the six new events has a consumer in this deployment — modules 08,
// 09, 10, 11, 12, 13 do not exist (D-071), module 03 does not exist, and module
// 06's declared consumptions of the remnant lifecycle events are projections it
// already holds synchronously where D-065 seams apply. They are emitted anyway:
// the mutation is real and the outbox row is the durable record a later module
// consumes from. Emission is pinned in the integration suite (the D-071
// wording), which can read the outbox; a walkthrough speaks HTTP and cannot.

const Uuid = z.guid();
/** Exact decimal text of a numeric(14,3) quantity (D-041 — never a float). */
const Qty = z.string();
const MinorMoney = z.number().int();
const Currency = z.string().length(3);

/** spec 05 §6 — `waste.category_updated`. Consumers: 18 (floor master-data
 * cache bump), 04 (floor category grid refresh), 13 (analytics).
 *
 * `changed_fields` is the §6 payload field; it carries the COLUMN names that
 * actually moved, so a consumer can decide whether the change matters to it
 * (a `sort_order` nudge does not invalidate a cached scrap-reason mapping). */
export const WasteCategoryUpdatedPayload = z.object({
  category_id: Uuid,
  code: z.string(),
  changed_fields: z.array(z.string()),
  is_active: z.boolean(),
});

/** spec 05 §6 — every waste occurrence. Consumers: 11, 12, 13, 04, 08 (when
 * cause blames_machine), 10 (when cause blames_supplier) — none exist (D-071);
 * emission pinned in the integration suite. `work_center_id` rides the payload
 * (D-101: the machine grain is the work centre in P2). */
export const WasteEventRecordedPayload = z.object({
  waste_event_id: Uuid,
  event_no: z.string(),
  category_code: z.string(),
  cause_code: z.string().nullable(),
  source: z.string(),
  item_type: z.enum(["material", "product"]),
  item_id: Uuid,
  lot_id: Uuid.nullable(),
  qty: Qty,
  uom: z.string(),
  cost_minor: MinorMoney,
  currency: Currency,
  work_order_id: Uuid.nullable(),
  work_center_id: Uuid.nullable(),
  machine_id: Uuid.nullable(),
  shift_instance_id: Uuid.nullable(),
  originating_shift_instance_id: Uuid.nullable(),
  disposition: z.string(),
});

/** spec 05 §6 — a reversal pair was created; the pair nets to zero. */
export const WasteEventReversedPayload = z.object({
  waste_event_id: Uuid,
  reversal_event_id: Uuid,
  reason: z.string(),
});

/** spec 05 §6 — a remnant entered the registry. Consumers: 06 (marks unit
 * registered), 09 (quote availability cache), 13 — none subscribe today. */
export const WasteRemnantRegisteredPayload = z.object({
  remnant_id: Uuid,
  remnant_no: z.string(),
  kind: z.string(),
  item_id: Uuid,
  lot_id: Uuid.nullable(),
  stock_unit_id: Uuid,
  length_m: Qty,
  diameter_mm: Qty.nullable(),
  price_class_code: z.string().nullable(),
  facility_id: Uuid,
});

export const WasteRemnantReservedPayload = z.object({
  remnant_id: Uuid,
  reserved_for_type: z.string(),
  reserved_for_id: Uuid,
  expires_at: z.string().nullable(),
});

export const WasteRemnantReleasedPayload = z.object({
  remnant_id: Uuid,
  reason: z.string(),
});

export const WasteRemnantClosedPayload = z.object({
  remnant_id: Uuid,
  final_status: z.enum(["consumed", "sold", "scrapped"]),
  consumed_ref_type: z.string().nullable(),
  consumed_ref_id: Uuid.nullable(),
});

export const wasteProducerEvents = {
  "waste.category_updated": {
    payload: WasteCategoryUpdatedPayload,
    consumers: ["18", "04", "13"],
  },
  // 05-B — the six ledger/remnant events, each with its emitter in the same
  // change (waste-events.ts).
  "waste.event_recorded": {
    payload: WasteEventRecordedPayload,
    consumers: ["11", "12", "13", "04", "08", "10"],
  },
  "waste.event_reversed": {
    payload: WasteEventReversedPayload,
    consumers: ["11", "13"],
  },
  "waste.remnant_registered": {
    payload: WasteRemnantRegisteredPayload,
    consumers: ["06", "09", "13"],
  },
  "waste.remnant_reserved": {
    payload: WasteRemnantReservedPayload,
    consumers: ["06", "09"],
  },
  "waste.remnant_released": {
    payload: WasteRemnantReleasedPayload,
    consumers: ["06", "09"],
  },
  "waste.remnant_closed": {
    payload: WasteRemnantClosedPayload,
    consumers: ["06", "13"],
  },
} as const;

export type WasteEventName = keyof typeof wasteProducerEvents;

export const wasteProducerEventNames = Object.keys(wasteProducerEvents) as WasteEventName[];

/**
 * Events module 05 CONSUMES (spec 05 §6 "Consumed"), wired by slice 05-B's two
 * subscribers (`waste-intake.subscriber.ts` / `waste-inventory.subscriber.ts`)
 * — async, at-least-once, unordered; every apply dedupes on the source
 * `event_id` in `waste_processed_events` (§9.17, D-044) and is commutative.
 *
 * NOT here, deliberately:
 *  * `production.work_order_released` / `work_order_closed` — the §3.3
 *    variance snapshot/close consumers belong to slice 05-D with §2.4/§2.5.
 *  * `production.work_order_completed` — §3.6's by-product batch birth
 *    (amended off `qty_reported`, D-103) lands with 05-D's `pvc_batches`.
 *  * `inventory.lot_created` — the regrind write-back correlation is DELETED
 *    by the D-065 in-txn regrind seam (05-D; the id comes back on the call).
 *  * The 03/09/10/11 producers (`planning.*`, `sales.*`, `procurement.*`,
 *    `finance.*`) — producers unbuilt; declared in spec §6, UNWIRED-listed.
 */
export const wasteConsumedEventNames = [
  "production.qty_reported",
  "production.calibration_recorded",
  "production.material_issued",
  "production.defect_handoff_flagged",
  "production.defect_handoff_disposed",
  "inventory.unit_cut",
  "inventory.movement_recorded",
  "inventory.unit_remeasured",
  "inventory.count_posted",
] as const;
